class PanelMenu extends HTMLElement {
  constructor() {
    super();

    const desktopWidth = this.dataset.desktopWidth;
    this.mediaQuery = globalThis.matchMedia(`(min-width: ${desktopWidth}px)`);

    // attributes set via theme settings.
    this.topLevelHover = this.dataset.topLevelHover != undefined;
    this.subLevelHover = this.dataset.subLevelHover != undefined;

    // elements
    this.links = this.querySelectorAll('[data-panel-link]');
    this.topLevelLinks = this.querySelectorAll('[data-panel-link-level="0"]');

    // note, these match all links whose parent is data-level-1, ie level-2, level-3 as well
    this.subLevelLinks = this.querySelectorAll(
      '[data-level="1"] [data-panel-link]'
    );
    this.subLevelDesktopNonPanelLinks = this.querySelectorAll(
      '.desktop [data-level="1"] a:not([data-panel-link],.panel-menu-product-card)'
    );

    this.panels = this.querySelectorAll('[data-panel]');
    this.goBackOneLevelButtons = this.querySelectorAll(
      '[data-panel-menu-back]'
    );
    this.closeButtons = this.querySelectorAll('[data-panel-menu-close]');
    this.header = document.querySelector('sticky-header');

    this.enableHoverBasedOnSectionSettings();
    this.searchInputWrapper = document.querySelector(
      '[data-search-input-wrapper]'
    );

    for (const link of this.links) {
      link.addEventListener('click', event => {
        this.toggleLinkChildPanel(event);
      });
    }

    for (const button of this.goBackOneLevelButtons) {
      button.addEventListener('click', event => {
        this.goBackOneLevel(event.target.closest('[data-panel]'));
      });
    }

    for (const button of this.closeButtons) {
      button.addEventListener('click', () => {
        this.closeMenu();
      });
    }

    for (const link of this.topLevelLinks) {
      link.addEventListener('click', () => {
        this.header.scrollSoHeaderBarIsTop();
      });
    }

    this.mainContent = document.querySelector('#MainContent');
    this.mainContent.addEventListener('click', event => {
      // if the click is on any part of the header (includes the menu), do nothing
      if (event.target.closest('.site-header')) return;

      // if the menu is open; close it.
      if (this.dataset.activeLevel) {
        this.closeMenu();
      }
    });
  }

  connectedCallback() {
    // Close the menu when the flyout cart is opened
    document.addEventListener('pxl:flyoutCart:toggleState', event => {
      const { open } = event.detail;
      if (!open) return;

      this.closeMenu();
    });
  }
  /**
   * Opens the target panel, and closes all other panels of the same or greater level.
   * @param {Event} event - event from the click event on a menu item
   * @returns {void}
   */
  toggleLinkChildPanel(event) {
    event.preventDefault();
    const menuItem = event.target.closest('[data-menu-item]');
    const targetPanel = menuItem.querySelector(':scope > [data-panel]');

    if (!targetPanel) return;

    const level = targetPanel.dataset.level;
    this.setLevel(level);

    if (level === '1') {
      const predictiveSearch = document.querySelector('predictive-search');
      predictiveSearch?.closePredictiveSearch();
    }

    // close all panels of the same, or greater level
    for (const panel of this.panels) {
      if (panel.dataset.level >= level) {
        this.closePanel(panel);
      }

      // remove 'top' attribute from all panels
      panel.removeAttribute('top');
    }

    // open the target panel
    this.openPanel(targetPanel);

    // if the target panel has a promo panel, open that too
    if (
      this.mediaQuery.matches &&
      Object.hasOwn(event.target.dataset, 'panelHasPromo')
    ) {
      // The promo panel the next sibling of the panel
      const promoPanel =
        targetPanel.parentElement.querySelector('[data-promo-panel]');
      targetPanel.removeAttribute('top');
      this.openPanel(promoPanel);
    }
  }

  /**
   * Sets or removes the data-active-level attribute on the menu element.
   * @param {Number} level
   * @returns {void}
   */
  setLevel(level) {
    if (level >= 0) {
      level = level + '';
      this.dataset.activeLevel = level;
    } else {
      delete this.dataset.activeLevel;
    }
  }

  /**
   * Toggles the first panel of the mobile menu open/closed.
   * @returns {void}
   */
  toggleMobileMenu() {
    const menuPanelLevelZero = this.querySelector('.panel-level-0');
    if (menuPanelLevelZero.hasAttribute('open')) {
      this.closePanel(menuPanelLevelZero);
    } else {
      this.openPanel(menuPanelLevelZero);
    }
  }

  /**
   * Opens a panel and sets other relevant attributes.
   * @param {Node} panelElement
   * @returns {void}
   */
  openPanel(panelElement) {
    panelElement.setAttribute('open', '');
    panelElement.setAttribute('top', '');
    this.setLevel(panelElement.dataset.level);
  }

  /**
   * Closes a panel and sets other relevant attributes.
   * @param {Node} panelElement
   * @param {Number} level (optional) - the level of the panel to close. Defaults to panelElement.dataset.level - 1
   * @returns {void}
   */
  closePanel(panelElement, level) {
    panelElement.removeAttribute('open');
    panelElement.removeAttribute('top');
    level = level || panelElement.dataset.level - 1;
    this.setLevel(level);
  }

  /**
   * Sets the 'top' attribute on the parent panel of the given panel.
   * @param {Node} panelElement
   * @returns {void}
   */
  setParentPanelToTop(panelElement) {
    const parentPanel = panelElement.parentElement.closest('[data-panel]');
    parentPanel.setAttribute('top', '');
  }

  /**
   * Closes the current panel, and opens the parent panel.
   * @param {Node} panelElement
   * @returns {void}
   */
  goBackOneLevel(panelElement) {
    this.closePanel(panelElement);
    this.setParentPanelToTop(panelElement);
  }

  /**
   * Open the passed panel, and close others.
   * @param {HTMLElement} panelElement - the panel to change to
   */
  changeOpenPanelTo(panelElement) {
    const openPanel = this.querySelector('[data-panel][top]');
    this.closePanel(openPanel);

    // check to see if we also need to close the parent panel
    const parentPanel = openPanel.parentElement.closest('[data-panel]');
    if (parentPanel.dataset.level > panelElement.dataset.level) {
      this.closePanel(parentPanel);
    }

    this.openPanel(panelElement);
  }

  /**
   * Closes the whole menu including all panels.
   * @returns {void}
   */
  closeMenu() {
    for (const panel of this.panels) {
      this.closePanel(panel);
      this.setLevel(-1);
    }
  }

  /**
   * If set in theme settings, hovering on top/sub level links has the same effect as clicking on them.
   * @returns {void}
   */
  enableHoverBasedOnSectionSettings() {
    if (this.topLevelHover || this.subLevelHover) {
      if (this.topLevelHover) {
        this.addMouseoverListeners(this.topLevelLinks, true);
      }

      if (this.subLevelHover) {
        this.addMouseoverListeners(this.subLevelLinks);

        /* If we hover over a link in the menu, and it opens a panel, the we hover over a different link in the 
        menu, that doesn't open a panel (e.g. links to a page), then close the open panel*/
        for (const link of this.subLevelDesktopNonPanelLinks) {
          link.addEventListener('mouseover', event => {
            if (this.mediaQuery.matches) {
              // if the panel this link is on is not the top one, then
              // close other panels and open this one
              const openPanel = this.querySelector('[data-panel][top]');
              const parentPanel = event.target.closest('[data-panel]');
              if (parentPanel != openPanel) {
                this.changeOpenPanelTo(parentPanel);
              }
            }
          });
        }
      }
    }
  }

  /**
   * Hovering over the elements passed will open the child panel on desktop.
   * @param {Array{Element}} links
   * @returns {void}
   */
  addMouseoverListeners(links, isTopLevel = false) {
    for (const link of links) {
      link.addEventListener('mouseover', event => {
        if (this.mediaQuery.matches) {
          isTopLevel && this.header.scrollSoHeaderBarIsTop();
          this.toggleLinkChildPanel(event);
        }
      });
    }
  }
}

customElements.get('panel-menu') ||
  customElements.define('panel-menu', PanelMenu);
